/**
 * PresetDropdown Component
 *
 * A compact dropdown alternative to the Segmented preset control for narrow viewports.
 * Uses Ant Design Select to provide preset selection when there isn't room for
 * the full Segmented control.
 *
 * Features:
 * - Icon-only trigger (maximizes narrow viewport space)
 * - Tooltip shows active preset label on hover
 * - Full icon + label in dropdown options
 * - Disabled state for incompatible presets in trash view
 * - 32px height matching other toolbar elements
 * - CSS-only visibility switching (no JS flicker during resize)
 *
 * @example
 * ```tsx
 * <PresetDropdown
 *   presets={presets.options}
 *   selectedPreset={selectedPreset}
 *   onPresetChange={handlePresetChange}
 *   showDeleted={showDeleted}
 *   trashIncompatiblePresets={['active', 'expired']}
 * />
 * ```
 */

import { Select, Tooltip } from 'antd'
import * as Icons from '@ant-design/icons'
import type { PresetOption } from '@/services/metadataService'

import './PresetDropdown.css'

export interface PresetDropdownProps {
  /** Preset options from metadata */
  presets: PresetOption[]
  /** Currently selected preset key */
  selectedPreset: string | null
  /** Handler for preset change */
  onPresetChange: (presetKey: string) => void
  /** Whether trash view is active (affects which presets are disabled) */
  showDeleted?: boolean
  /** Preset keys that are incompatible with trash view */
  trashIncompatiblePresets?: string[]
}

// Helper to render icons by name from Ant Design icons
function renderIcon(iconName?: string | null) {
  if (!iconName) return null
  const IconComponent = (Icons as any)[iconName]
  return IconComponent ? <IconComponent /> : null
}

/**
 * Compact preset dropdown for narrow viewports.
 * CSS handles visibility switching between this and the Segmented control.
 */
export function PresetDropdown({
  presets,
  selectedPreset,
  onPresetChange,
  showDeleted = false,
  trashIncompatiblePresets = [],
}: PresetDropdownProps) {
  // Find the selected preset for tooltip
  const activePreset = presets.find((p) => p.key === selectedPreset)

  // Build options for Select (dropdown shows icon + label)
  const options = presets.map((preset) => {
    const isDisabledInTrash = showDeleted && trashIncompatiblePresets.includes(preset.key)

    return {
      value: preset.key,
      disabled: isDisabledInTrash,
      label: (
        <span className="preset-dropdown-option">
          {renderIcon(preset.icon)}
          <span className="preset-dropdown-label">{preset.label}</span>
        </span>
      ),
    }
  })

  return (
    <Tooltip title={activePreset?.label ?? 'Filter'} placement="bottom">
      <Select
        className="toolbar-presets-dropdown"
        value={selectedPreset}
        onChange={onPresetChange}
        options={options}
        popupMatchSelectWidth={false}
        suffixIcon={null}
        // Icon-only trigger - show just the icon of the selected preset
        labelRender={({ value }) => {
          const preset = presets.find((p) => p.key === value)
          return (
            <span className="preset-dropdown-trigger">
              {renderIcon(preset?.icon)}
            </span>
          )
        }}
      />
    </Tooltip>
  )
}

export default PresetDropdown
